import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  Mic, FileText, Phone, Library, Sparkles,
  History, LogOut, Crown, BarChart3, Code2, HelpCircle, ScrollText, LinkedinIcon, Coins, Camera
} from "lucide-react";

const userNav = [
  { to: "/app", icon: Mic, label: "Interview", end: true, tid: "nav-interview" },
  { to: "/app?mode=coding", icon: Code2, label: "Coding Assistant", tid: "nav-coding" },
  { to: "/app/sessions", icon: History, label: "Session History", tid: "nav-sessions" },
  { to: "/app/resume", icon: ScrollText, label: "Resume Builder", tid: "nav-resume" },
  { to: "/app/linkedin", icon: LinkedinIcon, label: "LinkedIn Optimizer", tid: "nav-linkedin" },
  { to: "/app/coverletter", icon: FileText, label: "Cover Letter", tid: "nav-coverletter" },
  { to: "/app/headshot",  icon: Camera,   label: "Headshot Generator", tid: "nav-headshot" },
  { to: "/app/resources", icon: Library,  label: "Resource Hub",  tid: "nav-resources" },
  { to: "/app/referral",  icon: Coins,   label: "Earn with Us",  tid: "nav-referral" },
  { to: "/app/faq", icon: HelpCircle, label: "FAQ", tid: "nav-faq" },
];

const adminNav = [
  { to: "/app/admin", icon: BarChart3, label: "Admin Dashboard", end: true, tid: "nav-admin" },
];

function Avatar({ user }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (user?.avatar_url && !imgFailed) {
    return (
      <img src={user.avatar_url} alt={user.name}
        className="w-9 h-9 rounded-full object-cover shrink-0"
        onError={() => setImgFailed(true)} />
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0055FF] to-[#00A3FF] flex items-center justify-center text-xs font-bold shrink-0">
      {user?.name?.[0]?.toUpperCase()}
    </div>
  );
}

export default function Layout({ admin = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const items = admin ? adminNav : userNav;

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 flex flex-col bg-[#0A0A0A] sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => nav("/")}>
            <img src="/wingman-logo.png" alt="Wingman.AI" className="h-10 w-auto" />
            <span className="font-display font-bold tracking-tight text-lg">WINGMAN<span className="text-[#D3FF24]">.</span>AI</span>
          </div>
        </div>

        <div className="px-4 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Avatar user={user} />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                {(user?.plan === "elite" || user?.plan === "admin") && <Crown className="w-3 h-3 text-[#D3FF24]" />}
                {user?.plan}
              </div>
            </div>
          </div>
          {user?.plan === "free" && !admin && (
            <button
              onClick={() => nav("/pricing")}
              data-testid="upgrade-btn"
              className="mt-3 w-full bg-premium text-white text-xs font-semibold uppercase tracking-widest py-2.5 hover:brightness-110 transition-all"
            >
              Upgrade
            </button>
          )}
        </div>

        <nav className="flex-1 py-4 px-2 space-y-0.5">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">
            {admin ? "Admin" : "AI Wingman"}
          </div>
          {items.map((it) => {
            const isCoding = it.tid === "nav-coding";
            const isInterview = it.tid === "nav-interview";
            const onCodingPage = location.pathname === "/app" && location.search.includes("mode=coding");
            const isActive = isCoding
              ? onCodingPage
              : isInterview
              ? location.pathname === "/app" && !onCodingPage
              : location.pathname.startsWith(it.to);

            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.tid}
                className={() =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-all border-l-2 ${
                    isActive
                      ? "border-[#D3FF24] bg-white/[0.03] text-white"
                      : "border-transparent text-neutral-400 hover:text-white hover:bg-white/[0.02]"
                  }`
                }
              >
                <it.icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{it.label}</span>
              </NavLink>
            );
          })}

          {!admin && user?.role === "admin" && (
            <NavLink
              to="/app/admin"
              data-testid="nav-admin-shortcut"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-all border-l-2 mt-2 ${
                  isActive
                    ? "border-[#D3FF24] bg-white/[0.03] text-white"
                    : "border-transparent text-neutral-400 hover:text-white hover:bg-white/[0.02]"
                }`
              }
            >
              <BarChart3 className="w-4 h-4" strokeWidth={1.75} />
              <span>Admin Panel</span>
            </NavLink>
          )}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <button
            onClick={() => nav("/pricing")}
            data-testid="pricing-link"
            className="w-full flex items-center gap-3 px-3 py-2 text-xs text-neutral-500 hover:text-white"
          >
            <Sparkles className="w-3.5 h-3.5" /> Pricing
          </button>
          <button
            onClick={() => { logout(); nav("/"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 text-xs text-neutral-500 hover:text-[#FF3B30]"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
